import type { StatusTone } from '@vnetwork/ui/data';
import type { ContractStatus, ContractRole, ContractDirection } from '@/api/contracts';

/**
 * Contract domain formatting — status tones + human labels for the standalone
 * Contracts module. Mirrors the legacy `CONTRACT_STATUS` / `CONTRACT_ROLE_OPTIONS`
 * / `CONTRACT_DIRECTION_OPTIONS` colour + label intent on the @ui tone palette.
 * `retainted` is the backend's real (misspelled) enum value — kept verbatim.
 */

/** Contract status → @ui `StatusPill`/`Badge` tone. */
export function contractStatusTone(status?: string): StatusTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'exchanging':
      return 'info';
    case 'uploading':
      return 'warning';
    case 'pending':
      return 'info';
    case 'outdate':
    case 'expired':
    case 'retainted':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Human labels for `contract_status` (English-first, matches legacy intent). */
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  pending: 'Pending',
  exchanging: 'Exchanging',
  uploading: 'Uploading',
  active: 'Active',
  outdate: 'Outdated',
  retainted: 'Retained',
  expired: 'Expired',
};

/** Options for the status FilterBuilder select. */
export const CONTRACT_STATUS_OPTIONS: { value: ContractStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'exchanging', label: 'Exchanging' },
  { value: 'uploading', label: 'Uploading' },
  { value: 'active', label: 'Active' },
  { value: 'outdate', label: 'Outdated' },
  { value: 'retainted', label: 'Retained' },
];

/** Human labels for `contract_role`. */
export const CONTRACT_ROLE_LABELS: Record<ContractRole, string> = {
  customer: 'Customer',
  supplier: 'Supplier',
  reseller: 'Reseller',
  affiliate: 'Affiliate',
  collaborator: 'Collaborator',
};

export const CONTRACT_ROLE_OPTIONS: { value: ContractRole; label: string }[] = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'collaborator', label: 'Collaborator' },
];

/** Human labels for `contract_direction` (`earn` = output/money-in, `pay` = input/money-out). */
export const CONTRACT_DIRECTION_LABELS: Record<ContractDirection, string> = {
  earn: 'Output',
  pay: 'Input',
};

export const CONTRACT_DIRECTION_OPTIONS: { value: ContractDirection; label: string }[] = [
  { value: 'earn', label: 'Output' },
  { value: 'pay', label: 'Input' },
];

export function statusLabel(status?: string): string {
  if (!status) return '—';
  return CONTRACT_STATUS_LABELS[status as ContractStatus] ?? status;
}

export function roleLabel(role?: string): string {
  if (!role) return '—';
  return CONTRACT_ROLE_LABELS[role as ContractRole] ?? role;
}

export function directionLabel(direction?: string): string {
  if (!direction) return '—';
  return CONTRACT_DIRECTION_LABELS[direction as ContractDirection] ?? direction;
}
